import React, { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './Heading.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';
import {
  getBaseValue,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);

const heading = cva(styles.heading, {
  variants: {
    size: {
      xxs: styles['size--xxs'],
      xs: styles['size--xs'],
      sm: styles['size--sm'],
      md: styles['size--md'],
      lg: styles['size--lg'],
      xl: styles['size--xl'],
    },
    textAlign: {
      start: styles['align--start'],
      center: styles['align--center'],
      end: styles['align--end'],
    },
    truncate: {
      true: styles['truncate--true'],
      false: styles['truncate--false'],
    },
  },
  defaultVariants: {
    size: 'md',
    textAlign: 'start',
  },
});

type HeadingVariants = VariantProps<typeof heading>;
type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'legend';
type HeadingSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const DEFAULT_HEADING_SIZE_MAP: Record<HeadingLevel, HeadingSize> = {
  h1: 'xl',
  h2: 'lg',
  h3: 'md',
  h4: 'sm',
  h5: 'xs',
  h6: 'xxs',
  span: 'md', // Default for span elements
  legend: 'sm', // Default for legend elements
};

export interface BaseProps
  extends HeadingVariants,
    HTMLAttributes<HTMLHeadingElement | HTMLSpanElement | HTMLLegendElement> {
  /**
   * children
   */
  children: React.ReactNode;

  /**
   * The heading element to render
   */
  as?: HeadingLevel;

  /**
   * **[Responsive]** The size of the heading
   */
  size?: HeadingSize;

  /**
   * The foreground color of the heading
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];

  /**
   * **[Responsive]** The text alignment of the heading
   */
  textAlign?: 'start' | 'center' | 'end';

  /**
   * **[Responsive]** Truncate text with ellipsis
   * @default false
   */
  truncate?: boolean;
}

// ResponsivePropKeys are the keys of the props that can accept responsive values (base + breakpoint-specific)
type ResponsivePropKeys = 'size' | 'textAlign' | 'truncate';

export type HeadingProps = WithResponsiveProps<BaseProps, ResponsivePropKeys>;

export const Heading = ({
  children,
  as: Component = 'h1',
  size,
  foregroundColor = 'accentSecondary',
  textAlign = 'start',
  truncate = false,
  ...props
}: HeadingProps) => {
  // Determine the default size based on heading level, falling back to 'md' if not specified
  const defaultSize = DEFAULT_HEADING_SIZE_MAP[Component] ?? 'md';

  // Use the provided size or fall back to the default size based on heading level
  const effectiveSize = size ?? defaultSize;

  // Get the base values for size and textAlign to apply the correct default variants
  const baseSize = getBaseValue(effectiveSize, defaultSize);
  const baseAlign = getBaseValue(textAlign, 'start');
  const truncateBase = getBaseValue(truncate, false);

  // Generate responsive classes for size and textAlign
  const sizeResp = getResponsiveClasses(effectiveSize, 'size');
  const alignResp = getResponsiveClasses(textAlign, 'align');
  const truncateResp = getResponsiveClasses(truncate, 'truncate');

  return (
    <Component
      className={[
        heading({
          size: baseSize,
          textAlign: baseAlign,
          truncate: truncateBase,
        }),
        foregroundColorVariants({ foregroundColor }),
        ...sizeResp,
        ...alignResp,
        ...truncateResp,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Heading;
